import React from 'react';
import { calculatePortfolioCashflows, calculateIRR, formatCurrency, formatPercentage } from '../utils/calculations';

const CombinedSummary = ({ portfolios, investments }) => {
  if (!portfolios || !Array.isArray(portfolios) || portfolios.length === 0) {
    return <div>No portfolio data available</div>;
  }

  try {
    // Calculate combined totals
    let totalInvestment = 0;
    let totalInterest = 0;
    let totalCapitalReturn = 0;
    let maxMonth = 0;

    // Initialize combined cashflows
    portfolios.forEach(portfolio => {
      const investment = investments[portfolio.id] || 0;
      if (investment <= 0) return;

      totalInvestment += investment;

      // Find max month for this portfolio
      const portfolioMaxMonth = Math.max(...portfolio.projects.map(project => project.startMonth + 35));
      maxMonth = Math.max(maxMonth, portfolioMaxMonth);
    });

    const combinedCashflows = new Array(maxMonth).fill(0);

    // Calculate cashflows for each portfolio
    portfolios.forEach(portfolio => {
      const investment = investments[portfolio.id] || 0;
      if (investment <= 0) return;

        portfolio.projects.forEach(project => {
        if (project && typeof project.capitalDistribution === 'number') {
          const projectInvestment = investment * project.capitalDistribution;
          const projectEndMonth = project.startMonth + 35;

          // Add investment
          if (project.startMonth > 0 && project.startMonth <= maxMonth) {
            combinedCashflows[project.startMonth - 1] -= projectInvestment;
          }

          // Add monthly interest
          for (let month = project.startMonth; month < projectEndMonth && month <= maxMonth; month++) {
            const monthlyInterest = projectInvestment * project.interestRate / 12;
            combinedCashflows[month - 1] += monthlyInterest;
            totalInterest += monthlyInterest;
          }

          // Add capital return
          if (projectEndMonth <= maxMonth) {
            const exitMultiple = portfolio.id === 2 ? (project.exitMultiple || 1.0) : 1.0;
            const capitalReturn = projectInvestment * exitMultiple;
            combinedCashflows[projectEndMonth - 1] += capitalReturn;
            totalCapitalReturn += capitalReturn;
          }
        }
      });
    });

    const monthlyIrr = calculateIRR(combinedCashflows);
    const annualIrr = (Math.pow(1 + monthlyIrr, 12) - 1);

    const activePortfolios = portfolios.filter(p => investments[p.id] && investments[p.id] > 0).length;
    const totalPortfolios = portfolios.length;

    return (
      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-5">Resumen Completo de Inversión</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-8">
          <div className="relative overflow-hidden bg-white p-5 rounded-xl border border-red-200 shadow-sm group hover:shadow-md transition-all duration-200">
            <div className="absolute inset-0 bg-gradient-to-br from-red-50/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
            <div className="relative">
              <div className="text-sm text-gray-600 font-medium mb-2">Inversión Total</div>
              <div className="text-2xl font-semibold text-red-700">{formatCurrency(totalInvestment)}</div>
            </div>
          </div>
          
          <div className="relative overflow-hidden bg-white p-5 rounded-xl border border-emerald-200 shadow-sm group hover:shadow-md transition-all duration-200">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
            <div className="relative">
              <div className="text-sm text-gray-600 font-medium mb-2">Interes Total</div>
              <div className="text-2xl font-semibold text-emerald-700">{formatCurrency(totalInterest)}</div>
            </div>
          </div>
          
          <div className="relative overflow-hidden bg-white p-5 rounded-xl border border-blue-200 shadow-sm group hover:shadow-md transition-all duration-200">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
            <div className="relative">
              <div className="text-sm text-gray-600 font-medium mb-2">Regreso de Capital</div>
              <div className="text-2xl font-semibold text-blue-700">{formatCurrency(totalCapitalReturn)}</div>
            </div>
          </div>
          
          <div className="relative overflow-hidden bg-white p-5 rounded-xl border border-purple-200 shadow-sm group hover:shadow-md transition-all duration-200">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-50/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
            <div className="relative">
              <div className="text-sm text-gray-600 font-medium mb-2">Inversiones Activas</div>
              <div className="text-2xl font-semibold text-purple-700">{activePortfolios}/{totalPortfolios}</div>
            </div>
          </div>
          
          <div className="relative overflow-hidden bg-white p-5 rounded-xl border border-amber-200 shadow-sm group hover:shadow-md transition-all duration-200">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-50/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
            <div className="relative">
              <div className="text-sm text-gray-600 font-medium mb-2">TIR Anual</div>
              <div className="text-2xl font-semibold text-amber-700">{formatPercentage(annualIrr)}</div>
        </div>
          </div>
        </div>

        <div>
          <h4 className="text-lg font-semibold text-gray-800 mb-4">Flujo Mensual Combinado</h4>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="custom-scrollbar relative" style={{width: '100%'}}>
              <div style={{minWidth: 'max-content'}}>
                <table className="w-full text-sm">
                  <thead>
                    <tr>
                      <th className="sticky left-0 bg-gray-50 py-4 px-4 text-left font-semibold text-gray-600 border-b border-gray-200">Concepto</th>
                      {Array.from({ length: maxMonth }, (_, i) => (
                        <th key={i} className="bg-gray-50 py-4 px-4 text-right font-semibold text-gray-600 border-b border-gray-200">M{i + 1}</th>
                      ))}
                      <th className="bg-gray-50 py-4 px-4 text-right font-semibold text-gray-600 border-b border-gray-200">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="sticky left-0 bg-white py-3 px-4 text-left font-medium text-red-700 border-b border-gray-100">Inversión</td>
                      {Array.from({ length: maxMonth }, (_, month) => {
                        let investment = 0;
                        portfolios.forEach(portfolio => {
                          const portfolioInvestment = investments[portfolio.id] || 0;
                          if (portfolioInvestment <= 0) return;

                          portfolio.projects.forEach(project => {
                            if (project && typeof project.capitalDistribution === 'number') {
                              if (month + 1 === project.startMonth) {
                                investment -= portfolioInvestment * project.capitalDistribution;
                              }
                            }
                          });
                        });
                        return (
                          <td key={month} className="py-3 px-4 text-right border-b border-gray-100">
                            <span className={investment !== 0 ? 'text-red-600' : 'text-gray-400'}>
                              {investment !== 0 ? formatCurrency(investment) : '-'}
                            </span>
                          </td>
                        );
                      })}
                      <td className="py-3 px-4 text-right font-medium text-red-700 border-b border-gray-100 bg-gray-50">{formatCurrency(-totalInvestment)}</td>
                    </tr>
                    <tr>
                      <td className="sticky left-0 bg-white py-3 px-4 text-left font-medium text-emerald-700 border-b border-gray-100">Interés</td>
                      {Array.from({ length: maxMonth }, (_, month) => {
                        let interest = 0;
                        portfolios.forEach(portfolio => {
                          const portfolioInvestment = investments[portfolio.id] || 0;
                          if (portfolioInvestment <= 0) return;

                          portfolio.projects.forEach(project => {
                            if (project && typeof project.capitalDistribution === 'number') {
                              const projectEndMonth = project.startMonth + 35;
                              if (month + 1 >= project.startMonth && month + 1 < projectEndMonth) {
                                interest += (portfolioInvestment * project.capitalDistribution * project.interestRate / 12);
                              }
                            }
                          });
                        });
                return (
                          <td key={month} className="py-3 px-4 text-right border-b border-gray-100">
                            <span className={interest !== 0 ? 'text-emerald-600' : 'text-gray-400'}>
                              {interest !== 0 ? formatCurrency(interest) : '-'}
                            </span>
                          </td>
                        );
                      })}
                      <td className="py-3 px-4 text-right font-medium text-emerald-700 border-b border-gray-100 bg-gray-50">{formatCurrency(totalInterest)}</td>
                    </tr>
                    <tr>
                      <td className="sticky left-0 bg-white py-3 px-4 text-left font-medium text-blue-700 border-b border-gray-100">Retorno Capital</td>
                      {Array.from({ length: maxMonth }, (_, month) => {
                        let capitalReturn = 0;
                        portfolios.forEach(portfolio => {
                          const portfolioInvestment = investments[portfolio.id] || 0;
                          if (portfolioInvestment <= 0) return;

                          portfolio.projects.forEach(project => {
                            if (project && typeof project.capitalDistribution === 'number') {
                              const projectEndMonth = project.startMonth + 35;
                              if (month + 1 === projectEndMonth) {
                                const exitMultiple = portfolio.id === 2 ? (project.exitMultiple || 1.0) : 1.0;
                                capitalReturn += portfolioInvestment * project.capitalDistribution * exitMultiple;
                              }
                            }
                          });
                        });
                        return (
                          <td key={month} className="py-3 px-4 text-right border-b border-gray-100">
                            <span className={capitalReturn !== 0 ? 'text-blue-600' : 'text-gray-400'}>
                              {capitalReturn !== 0 ? formatCurrency(capitalReturn) : '-'}
                            </span>
                          </td>
                        );
                      })}
                      <td className="py-3 px-4 text-right font-medium text-blue-700 border-b border-gray-100 bg-gray-50">{formatCurrency(totalCapitalReturn)}</td>
                    </tr>
                    <tr>
                      <td className="sticky left-0 bg-white py-3 px-4 text-left font-semibold text-gray-900 border-t border-gray-200">Total</td>
                      {combinedCashflows.map((monthTotal, month) => (
                        <td key={month} className="py-3 px-4 text-right font-medium border-t border-gray-200">
                          <span className={`${monthTotal > 0 ? 'text-emerald-600' : monthTotal < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                            {formatCurrency(monthTotal)}
                          </span>
                        </td>
                      ))}
                      <td className="py-3 px-4 text-right font-semibold text-gray-900 border-t border-gray-200 bg-gray-50">
                        {formatCurrency(totalInterest + totalCapitalReturn - totalInvestment)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  } catch (error) {
    console.error('Error in CombinedSummary:', error);
    return <div>Error calculating combined summary</div>;
  }
};

export default CombinedSummary; 